import { ResourceType } from '../../enums';

/** What an in-app preview can render inline; `null` means "no inline preview". */
export type AssetPreviewKind =
  'pdf' | 'image' | 'video' | 'audio' | 'office' | null;

export interface AssetPreviewKindInput {
  /** File extension, any case, e.g. `PDF`, `jpg`. */
  fileType?: string | null;
  /** Setvi `ResourceType` number. */
  resourceTypeId: number;
}

// Formats a browser renders natively. Anything else of the same resource type
// (TIFF, PSD, …) gets the "not available" state instead of a broken preview.
const IMAGE_TYPES = ['JPG', 'JPEG', 'PNG', 'GIF', 'WEBP', 'SVG', 'BMP', 'AVIF'];
// MOV is attempted too: Safari and most Chrome builds play H.264 MOV; the
// player falls back to the "not available" state when the browser cannot.
const VIDEO_TYPES = ['MP4', 'WEBM', 'OGV', 'M4V', 'MOV'];
const AUDIO_TYPES = ['MP3', 'WAV', 'OGG', 'OGA', 'M4A', 'AAC'];
// Rendered through the Office Online viewer, which needs a public url.
const OFFICE_TYPES = ['DOC', 'DOCX', 'XLS', 'XLSX', 'PPT', 'PPTX'];
const OFFICE_RESOURCE_TYPES = [
  ResourceType.WordDocument,
  ResourceType.Spreadsheet,
  ResourceType.Presentation
];

/**
 * Which inline preview fits a file, decided by the extension first and the
 * Setvi resource type as a fallback when the extension is missing. Web pages,
 * fonts and anything else return `null`: the preview shows a "not available"
 * state for them.
 */
export const getAssetPreviewKind = ({
  fileType,
  resourceTypeId
}: AssetPreviewKindInput): AssetPreviewKind => {
  // Tolerates a leading dot (`.pdf`) and any case.
  const ext = (fileType || '').trim().replace(/^\./, '').toUpperCase();
  const type = Number(resourceTypeId);

  if (ext === 'PDF' || (!ext && type === ResourceType.PdfDocument))
    return 'pdf';
  if (IMAGE_TYPES.includes(ext) || (!ext && type === ResourceType.Images))
    return 'image';
  if (VIDEO_TYPES.includes(ext) || (!ext && type === ResourceType.Video))
    return 'video';
  if (AUDIO_TYPES.includes(ext) || (!ext && type === ResourceType.Audio))
    return 'audio';
  if (
    OFFICE_TYPES.includes(ext) ||
    (!ext && OFFICE_RESOURCE_TYPES.includes(type))
  )
    return 'office';

  return null;
};
